import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { authAPI } from '../services/api';
import { isWebAuthnSupported, loginWithPasskey } from '../services/webauthn';
import { toast } from 'sonner';
import { Mail, KeyRound, ArrowLeft, Loader2, Fingerprint } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // OTP login state
  const [otpStep, setOtpStep] = useState('email'); // 'email' | 'otp'
  const [loginEmail, setLoginEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const otpRefs = useRef([]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const timer = setTimeout(() => setResendTimer(r => r - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendTimer]);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!loginEmail.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      const { data } = await authAPI.requestOTP(loginEmail.trim().toLowerCase());
      setUserName(data.user_name || 'User');
      setOtpStep('otp');
      setResendTimer(60);
      if (data.email_sent) {
        toast.success('Verification code sent to your email');
      } else {
        toast.info('Please check your email for the verification code.', { duration: 8000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    const email = loginEmail.trim().toLowerCase();
    if (!email) { toast.error('Enter your email first'); return; }
    setLoading(true);
    try {
      const { user } = await loginWithPasskey(email);
      toast.success(`Welcome back, ${user.name || 'Resident'}!`);
      const roleRouteMap = {
        resident: '/resident', guard: '/guard',
        management: '/management', super_admin: '/super-admin',
      };
      navigate(roleRouteMap[user.role] || '/', { replace: true });
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || '';
      if (detail === 'No passkey found for this email') {
        toast.info('No Face ID / Touch ID set up for this email yet. Log in with email OTP first, then enable it from Profile.', { duration: 6000 });
      } else if (error.name === 'NotAllowedError') {
        toast.error('Face ID / passkey cancelled');
      } else {
        toast.error(detail || 'Passkey login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6);
      const newValues = [...otpValues];
      digits.split('').forEach((d, i) => {
        if (i + index < 6) newValues[i + index] = d;
      });
      setOtpValues(newValues);
      const nextFocus = Math.min(index + digits.length, 5);
      otpRefs.current[nextFocus]?.focus();
      if (newValues.every(v => v !== '')) {
        setTimeout(() => submitOTP(newValues.join('')), 100);
      }
      return;
    }
    if (value && !/^\d$/.test(value)) return;
    const newValues = [...otpValues];
    newValues[index] = value;
    setOtpValues(newValues);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
    if (newValues.every(v => v !== '')) {
      setTimeout(() => submitOTP(newValues.join('')), 100);
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const submitOTP = async (otpString) => {
    if (loading) return;
    setLoading(true);
    try {
      const { data } = await authAPI.verifyOTP(loginEmail.trim().toLowerCase(), otpString, keepLoggedIn);
      const storage = keepLoggedIn ? localStorage : sessionStorage;
      storage.setItem('token', data.token);
      storage.setItem('user', JSON.stringify(data.user));
      if (keepLoggedIn) {
        localStorage.setItem('keepLoggedIn', 'true');
      } else {
        localStorage.removeItem('keepLoggedIn');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
      toast.success(`Welcome back, ${data.user.name}!`);
      const roleRoutes = { guard: '/guard', resident: '/resident', management: '/management', super_admin: '/super-admin' };
      navigate(roleRoutes[data.user.role] || '/resident');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
      setOtpValues(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = (e) => {
    e.preventDefault();
    const otpString = otpValues.join('');
    if (otpString.length !== 6) {
      toast.error('Please enter the complete 6-digit code');
      return;
    }
    submitOTP(otpString);
  };

  const handleResendOTP = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      const { data } = await authAPI.requestOTP(loginEmail.trim().toLowerCase());
      setResendTimer(60);
      if (data.email_sent) {
        toast.success('New code sent to your email');
      } else {
        toast.info('Please check your email for the verification code.', { duration: 8000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gk-navy via-gk-navy-light to-gk-navy flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/icon-192.png" alt="GateKeeper Pro" className="w-16 h-16 rounded-xl mx-auto mb-4 shadow-lg shadow-cyan-500/20" />
          <h1 className="text-3xl font-bold text-white">GateKeeper Pro</h1>
          <p className="text-slate-400 text-sm mt-1">Secure Community Management</p>
        </div>

        <Card className="shadow-2xl border-0 bg-white/95 backdrop-blur-sm">
          {otpStep === 'email' ? (
            <form onSubmit={handleRequestOTP}>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-gk-navy">
                  <Mail className="w-5 h-5" />
                  Sign in with Email
                </CardTitle>
                <CardDescription>Enter your registered email to receive a login code</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email Address</Label>
                  <Input
                    id="login-email"
                    data-testid="login-email-input"
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    autoFocus
                    className="h-11"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="keep-logged-in"
                    data-testid="keep-logged-in-checkbox"
                    type="checkbox"
                    checked={keepLoggedIn}
                    onChange={(e) => setKeepLoggedIn(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-300 text-gk-navy-light focus:ring-gk-navy-light cursor-pointer"
                  />
                  <Label htmlFor="keep-logged-in" className="text-sm text-slate-600 cursor-pointer select-none">
                    Keep me logged in
                  </Label>
                </div>
                <Button
                  data-testid="request-otp-button"
                  type="submit"
                  className="w-full bg-gk-navy-light hover:bg-gk-navy text-white h-11"
                  disabled={loading}
                >
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending code...</> : 'Send Login Code'}
                </Button>

                <div className="relative py-1">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-slate-200"></span>
                  </div>
                  <div className="relative flex justify-center text-[11px] uppercase tracking-wide">
                    <span className="bg-white/95 px-2 text-slate-400">or</span>
                  </div>
                </div>

                <Button
                  data-testid="google-signin-button"
                  type="button"
                  variant="outline"
                  className="w-full h-11 border-slate-300 hover:bg-slate-50 text-slate-700 font-medium"
                  onClick={() => {
                    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS
                    // OR REDIRECT URLS, THIS BREAKS THE AUTH.
                    const redirectUrl = window.location.origin + '/auth/google-callback';
                    window.location.href =
                      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

                {isWebAuthnSupported() && (
                  <Button
                    data-testid="passkey-signin-button"
                    type="button"
                    variant="outline"
                    className="w-full h-11 border-slate-300 hover:bg-slate-50 text-slate-700 font-medium"
                    disabled={loading || !loginEmail.trim()}
                    onClick={handlePasskeyLogin}
                  >
                    <Fingerprint className="w-4 h-4 mr-2 text-gk-teal" />
                    Sign in with Face ID / Fingerprint
                  </Button>
                )}
              </CardContent>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP}>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-gk-navy">
                  <KeyRound className="w-5 h-5" />
                  Enter Verification Code
                </CardTitle>
                <CardDescription>
                  Hi <strong>{userName}</strong>, we sent a 6-digit code to <strong>{loginEmail}</strong>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-center gap-2">
                  {otpValues.map((val, i) => (
                    <input
                      key={`otp-${i}`}
                      ref={el => otpRefs.current[i] = el}
                      data-testid={`otp-input-${i}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={val}
                      onChange={(e) => handleOTPChange(i, e.target.value)}
                      onKeyDown={(e) => handleOTPKeyDown(i, e)}
                      className="w-11 h-13 text-center text-xl font-bold border-2 border-slate-200 rounded-lg focus:border-gk-teal focus:ring-2 focus:ring-gk-teal/20 outline-none transition-all"
                      autoFocus={i === 0}
                    />
                  ))}
                </div>

                <Button
                  data-testid="verify-otp-button"
                  type="submit"
                  className="w-full bg-gk-teal hover:bg-gk-teal/90 text-white h-11"
                  disabled={loading || otpValues.join('').length !== 6}
                >
                  {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</> : 'Verify & Login'}
                </Button>

                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    data-testid="back-to-email-button"
                    onClick={() => { setOtpStep('email'); setOtpValues(['', '', '', '', '', '']); }}
                    className="text-slate-500 hover:text-gk-navy flex items-center gap-1 transition-colors"
                  >
                    <ArrowLeft className="w-3 h-3" /> Change email
                  </button>
                  <button
                    type="button"
                    data-testid="resend-otp-button"
                    onClick={handleResendOTP}
                    disabled={resendTimer > 0 || loading}
                    className={`transition-colors ${resendTimer > 0 ? 'text-slate-400' : 'text-gk-teal hover:text-gk-teal/80 font-medium'}`}
                  >
                    {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend code'}
                  </button>
                </div>
              </CardContent>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
